type CustomHeaders = Record<string, string | undefined>;

export interface ApiClientOptions {
  method?: 'GET' | 'POST' | 'DELETE' | 'PATCH' | 'PUT';
  headers?: CustomHeaders;
  withApiHost?: boolean;
  withAuthorization?: boolean;
  authorizePageQuery?: string;
  body?: BodyInit | null | undefined;
  credentials?: RequestCredentials;
  url?: string;
  signal?: AbortSignal;
  cache?: RequestCache;
}

export type TApiAuthClientProps = {
  headers?: CustomHeaders;
  baseUrl?: string;
  isIframe?: boolean;
  onUnauthorizedWhenInIframe?: () => void;
  withApiHost?: boolean;
};

export type TApiAuthClientInstance = {
  fetch: (endpoint: string, options?: ApiClientOptions) => Promise<Response>;
  headers: CustomHeaders;
  setAccessToken: (token: string) => void;
  getAccessToken: () => string;
};

const EXPIRATION_THRESHOLD = 30;

const decodeJWT = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );

    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

const createApiAuthClient = ({
  baseUrl = '',
  withApiHost = false,
  isIframe = false,
  headers: instanceHeaders = {},
  onUnauthorizedWhenInIframe,
}: TApiAuthClientProps): TApiAuthClientInstance => {
  let ACCESS_TOKEN = '';

  const setAccessToken = (token: string) => {
    ACCESS_TOKEN = token;
  };

  const customFetch = async (endpoint: string, options?: ApiClientOptions) => {
    const {
      url,
      headers: headersFromRequest = {},
      method = 'GET',
      withAuthorization = true,
      withApiHost: withApiHostOption = withApiHost,
      // this will pass to authorize endpoint - which will redirect the user to the desired page after success authentication
      authorizePageQuery = `?next=${window.location.pathname}${window.location.search}`,
      credentials = withApiHostOption ? 'include' : 'same-origin',
      ...rest
    } = options || {};

    const apiHost = withApiHostOption ? localStorage.getItem('apiHost') || '' : '';
    const domain = url || apiHost || baseUrl || '';
    const isRefreshEndpoint = endpoint === '/api/auth/refresh';

    const request = () => {
      // to avoid sending access_token to external endpoints (like aws)
      const authorizationHeader = withAuthorization
        ? ACCESS_TOKEN
          ? { Authorization: `Bearer ${ACCESS_TOKEN}` }
          : {}
        : {};

      const mergedHeaders: CustomHeaders = {
        ...instanceHeaders,
        ...headersFromRequest,
      };
      const finalHeaders: CustomHeaders = {};
      // send only headers with values
      Object.keys(mergedHeaders).forEach(key => {
        // allowing booleans but not allowing empty strings, undefined or null
        if (mergedHeaders[key] !== '' && mergedHeaders[key] !== undefined && mergedHeaders[key] !== null) {
          finalHeaders[key] = mergedHeaders[key];
        }
      });

      return fetch(`${domain}${endpoint}`, {
        method,
        credentials,
        ...rest,
        headers: {
          'X-Client': 'web',
          'X-CSRFToken': localStorage.getItem('csrfToken') || undefined,
          ...finalHeaders,
          ...authorizationHeader,
        } as any,
      });
    };

    const refreshAccessTokenFlow = async (isRefreshEndpoint: boolean = false) => {
      let responseAccessToken = new Response(null, { status: 401 });

      if (!isRefreshEndpoint) {
        // Trying to get new access token that will be saved in memory
        responseAccessToken = await fetch(`${domain}/api/auth/refresh`, {
          credentials: 'include',
        });
      }

      /////////////////////
      // THIS IS TEMPORARY - until all clients will roll-out to new authentication flow - until then - access_token.response will return 4XX (but not 401) to indicate old flow
      if (responseAccessToken.status !== 200 && responseAccessToken.status !== 401) {
        return response;
      }
      /////////////////////

      // we want to redirect in case refresh token is not available
      if (responseAccessToken.status === 401) {
        // in case we are in iframe - we want to send message to parent window that user is not authorized
        if (isIframe) {
          onUnauthorizedWhenInIframe?.();
          return response;
        }
        if (ACCESS_TOKEN) {
          window.location.replace(`${window.location.origin}/api/auth/logout`);
        } else {
          // not authorized and allow to get refresh token - redirecting to /api/auth/authorize endpoint with next param or current path
          window.location.href = `${domain}/api/auth/authorize${authorizePageQuery}`;
        }
        return response;
      } else {
        // got access token - assigning ACCESS_TOKEN with it
        try {
          const responseBody = (await responseAccessToken.json()) || {};
          ACCESS_TOKEN = responseBody?.access_token;
        } catch {
          ACCESS_TOKEN = '';
        }

        // if we are in iframe and refresh got 200 - we want to send set-cookie request
        if (isIframe) {
          await fetch(`${domain}/api/auth/set-cookie`, {
            credentials: 'include',
          });
        }

        // now that ACCESS_TOKEN exists - retry the request
        return await request();
      }
    };

    //// Expiration refresh logic start /////////////
    const decodedToken = decodeJWT(ACCESS_TOKEN);
    const currentTime = Math.floor(Date.now() / 1000);
    const isAboutToExpire = decodedToken ? decodedToken.exp - currentTime < EXPIRATION_THRESHOLD : false;

    if (isAboutToExpire) {
      return await refreshAccessTokenFlow();
    }
    //// Expiration refresh login end /////////////

    const response = await request();

    //// Authorization logic start ////////////////
    if (!withAuthorization) {
      return response;
    }

    // Not authorized
    if (response.status === 401) {
      return await refreshAccessTokenFlow(isRefreshEndpoint);
    }
    //// Authorization logic end //////////////////

    return response;
  };

  return {
    headers: instanceHeaders,
    setAccessToken,
    getAccessToken: () => ACCESS_TOKEN,
    fetch: customFetch,
  };
};

export default createApiAuthClient;
